import React, { useMemo, useState } from 'react';
import {
  PlusIcon,
  TrendingDownIcon,
  XIcon,
  PencilIcon,
  TrashIcon,
} from 'lucide-react';
import { Toast } from '../Toast';
import {
  useClientMeasurements,
  MeasurementRecord,
  useInvalidateMeasurements,
} from '../../hooks/useMeasurements';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/api';
import { useMutation } from '@tanstack/react-query';

interface MeasurementsTabProps {
  clientId: string;
}

type FormState = {
  date: string;
  weight: string;
  height: string;
  waist: string;
  notes: string;
};

const initialFormState: FormState = {
  date: '',
  weight: '',
  height: '',
  waist: '',
  notes: '',
};

function toInputDate(value?: string | null) {
  if (!value) return '';
  return value.split('T')[0] ?? value;
}

function formatDisplayDate(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function MeasurementsTab({
  clientId,
}: MeasurementsTabProps) {
  const { session } = useAuth();
  const invalidate = useInvalidateMeasurements(clientId);
  const [showForm, setShowForm] = useState(false);
  const [editingMeasurement, setEditingMeasurement] =
    useState<MeasurementRecord | null>(null);
  const [formData, setFormData] = useState<FormState>(initialFormState);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const measurementsQuery = useClientMeasurements(clientId);
  const measurements = measurementsQuery.data ?? [];

  const summary = useMemo(() => {
    if (!measurements.length) {
      return {
        currentWeight: null,
        currentBmi: null,
        lastUpdated: null,
        delta: null,
      };
    }
    const latest = measurements[0];
    const previous = measurements[1];
    const delta =
      latest?.weight_kg && previous?.weight_kg
        ? Number((latest.weight_kg - previous.weight_kg).toFixed(1))
        : null;
    return {
      currentWeight: latest?.weight_kg ?? null,
      currentBmi: latest?.bmi ?? null,
      lastUpdated: latest?.date ?? null,
      delta,
    };
  }, [measurements]);

  const resetForm = () => {
    setFormData(initialFormState);
    setEditingMeasurement(null);
    setError(null);
  };

  const openCreateForm = () => {
    resetForm();
    // Set today's date as default
    const today = new Date().toISOString().split('T')[0];
    setFormData({ ...initialFormState, date: today });
    setShowForm(true);
  };

  const openEditForm = (measurement: MeasurementRecord) => {
    setEditingMeasurement(measurement);
    setFormData({
      date: toInputDate(measurement.date),
      weight: measurement.weight_kg?.toString() ?? '',
      height: measurement.height_cm?.toString() ?? '',
      waist: measurement.waist_cm?.toString() ?? '',
      notes: measurement.note ?? '',
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }

      if (!formData.weight.trim()) {
        throw new Error('Weight is required');
      }

      const payload = {
        date: formData.date || undefined,
        weight_kg: formData.weight,
        height_cm: formData.height || undefined,
        waist_cm: formData.waist || undefined,
        note: formData.notes || undefined,
      };

      if (editingMeasurement) {
        return apiRequest(
          `/api/clients/${clientId}/measurements/${editingMeasurement.id}`,
          {
            method: 'PATCH',
            body: JSON.stringify(payload),
          },
          session.access_token,
        );
      }

      return apiRequest(
        `/api/clients/${clientId}/measurements`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        session.access_token,
      );
    },
    onSuccess: (data) => {
      const isEdit = Boolean(editingMeasurement);
      setToastMessage(isEdit ? 'Measurement updated' : 'Measurement saved');
      setShowForm(false);
      resetForm();
      invalidate();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to save measurement');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (measurementId: string) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      return apiRequest(
        `/api/clients/${clientId}/measurements/${measurementId}`,
        {
          method: 'DELETE',
        },
        session.access_token,
      );
    },
    onSuccess: () => {
      setToastMessage('Measurement deleted');
      invalidate();
    },
  });

  const handleDelete = (measurement: MeasurementRecord) => {
    if (
      deleteMutation.isPending ||
      !window.confirm('Delete this measurement entry?')
    ) {
      return;
    }
    deleteMutation.mutate(measurement.id);
  };

  const renderSummary = () => (
    <div className="bg-gradient-to-r from-[#5B8E87] to-[#4F7A74] rounded-2xl shadow-sm p-5 text-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-base">Progress Summary</h3>
        {summary.lastUpdated && (
          <span className="text-xs text-white text-opacity-80">
            Updated {formatDisplayDate(summary.lastUpdated)}
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-white text-opacity-80 text-xs mb-1">Current</p>
          <p className="text-2xl font-bold">
            {summary.currentWeight ? `${summary.currentWeight}kg` : '—'}
          </p>
        </div>
        <div>
          <p className="text-white text-opacity-80 text-xs mb-1">BMI</p>
          <p className="text-2xl font-bold">
            {summary.currentBmi ? summary.currentBmi : '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TrendingDownIcon className="w-4 h-4" />
          <span className="text-sm font-medium">
            {summary.delta
              ? summary.delta > 0
                ? `+${summary.delta}kg vs last`
                : `${summary.delta}kg vs last`
              : 'No change'}
          </span>
        </div>
      </div>
    </div>
  );

  const renderTable = () => {
    if (measurementsQuery.isLoading) {
      return (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center border border-gray-100">
          <p className="text-sm text-gray-500">Loading measurements...</p>
        </div>
      );
    }

    if (!measurements.length) {
      return (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center border border-gray-100">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">
            📉
          </div>
          <h3 className="text-lg font-semibold text-[#212121] mb-2">
            No measurements yet
          </h3>
          <p className="text-gray-600 mb-4">
            Record the first measurement to start tracking progress.
          </p>
          <button
            onClick={openCreateForm}
            className="bg-[#5B8E87] text-white px-6 py-2 rounded-lg font-medium"
          >
            Add Measurement
          </button>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
        <h3 className="font-semibold text-base text-[#212121] mb-4">
          Measurement History
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-600">
                <th className="py-3 font-medium">Date</th>
                <th className="py-3 font-medium text-center">Weight</th>
                <th className="py-3 font-medium text-center">Height</th>
                <th className="py-3 font-medium text-center">Waist</th>
                <th className="py-3 font-medium text-center">BMI</th>
                <th className="py-3 font-medium">Notes</th>
                <th className="py-3 font-medium text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {measurements.map((measurement) => (
                <tr
                  key={measurement.id}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  <td className="py-3 text-[#212121]">
                    {formatDisplayDate(measurement.date)}
                  </td>
                  <td className="py-3 text-center font-semibold text-[#5B8E87]">
                    {measurement.weight_kg ? `${measurement.weight_kg}kg` : '—'}
                  </td>
                  <td className="py-3 text-center text-gray-600">
                    {measurement.height_cm ? `${measurement.height_cm}cm` : '—'}
                  </td>
                  <td className="py-3 text-center text-gray-600">
                    {measurement.waist_cm ? `${measurement.waist_cm}cm` : '—'}
                  </td>
                  <td className="py-3 text-center text-gray-600">
                    {measurement.bmi ?? '—'}
                  </td>
                  <td className="py-3 text-gray-600">
                    {measurement.note || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => openEditForm(measurement)}
                        className="p-2 bg-gray-100 rounded-lg text-gray-600 hover:bg-gray-200"
                        aria-label="Edit measurement"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(measurement)}
                        className="p-2 bg-red-50 rounded-lg text-red-500 hover:bg-red-100"
                        aria-label="Delete measurement"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 space-y-4">
      {renderSummary()}
      {renderTable()}

      <button
        onClick={openCreateForm}
        className="fixed bottom-24 right-6 bg-[#C9A870] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#B89660] transition-all active:scale-95 z-20"
      >
        <PlusIcon className="w-6 h-6" />
      </button>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#212121]">
                {editingMeasurement ? 'Edit Measurement' : 'Add Measurement'}
              </h2>
              <button onClick={closeForm} className="text-gray-500">
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-2 rounded-xl">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[#212121] mb-2">
                    Weight (kg)
                  </label>
                  <input
                    type="number"
                    value={formData.weight}
                    onChange={(e) =>
                      setFormData({ ...formData, weight: e.target.value })
                    }
                    placeholder="65"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#212121] mb-2">
                    Height (cm)
                  </label>
                  <input
                    type="number"
                    value={formData.height}
                    onChange={(e) =>
                      setFormData({ ...formData, height: e.target.value })
                    }
                    placeholder="165"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Waist (cm)
                </label>
                <input
                  type="number"
                  value={formData.waist}
                  onChange={(e) =>
                    setFormData({ ...formData, waist: e.target.value })
                  }
                  placeholder="82"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#212121] mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder="Any observations..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all resize-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={closeForm}
                  className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => mutation.mutate()}
                  disabled={
                    mutation.isPending || deleteMutation.isPending || !formData.weight
                  }
                  className={`flex-1 font-semibold py-3 rounded-xl transition-colors ${
                    formData.weight
                      ? 'bg-[#5B8E87] text-white hover:bg-[#4F7A74]'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {mutation.isPending
                    ? 'Saving...'
                    : editingMeasurement
                    ? 'Save Changes'
                    : 'Save Entry'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
